import { makeObservable, observable } from 'mobx';
import {
  createModelSchema as createModelSchemaNative,
  Clazz,
  Props,
  ModelSchema,
  deserialize,
  ClazzOrModelSchema
} from 'serializr';
import type Context from 'serializr/lib/core/Context';

type Inject = <T>(name: symbol) => T;

export interface IEntityCache {
  getEntityById(cacheKey: string, id: any): any;
  clearEntities(cacheKey: string): void;
  deserialize<T>(schema: ClazzOrModelSchema<T>, jsonData: any | any[]): T;
}

export class EntityCache implements IEntityCache {
  @observable
  private cacheInternal = new Map<string, any>();

  constructor(private inject: Inject) {
    makeObservable(this);
  }

  get cache() {
    return this.cacheInternal;
  }

  getEntityById(cacheKey: string, id: any) {
    return this.cacheInternal.get(`${cacheKey}_${id}`);
  }

  clearEntities(cacheKey: string) {
    this.cacheInternal.forEach((val, key) => {
      if (key.startsWith(`${cacheKey}_`)) {
        this.cacheInternal.delete(key);
      }
    });
  }

  deserialize<T>(schema: ClazzOrModelSchema<T>, jsonData: any | any[]) {
    return deserialize(schema, jsonData, undefined, {
      cache: this.cacheInternal,
      inject: this.inject
    });
  }
}

interface DefaultContext extends Context {
  args: {
    inject: Inject;
  };
}

export function createModelSchema<T extends object>(
  clazz: Clazz<T>,
  props: Props,
  factory?: (context: DefaultContext) => T
): ModelSchema<T> {
  // eslint-disable-next-line new-cap
  const defFactory = factory || (() => new clazz());

  return createModelSchemaNative(clazz, props, context => {
    const { cache } = context.args;
    const className = (clazz as any).cacheKey || context.modelSchema.targetClass?.name;
    const idFieldName =
      Object.entries(context.modelSchema.props).find(([, s]) => {
        return typeof s === 'object' && !!s.identifier;
      })?.[0] ||
      Object.entries(context.modelSchema.extends?.props || {}).find(([, s]) => {
        return typeof s === 'object' && !!s.identifier;
      })?.[0];

    if (idFieldName && typeof idFieldName === 'string') {
      const cacheKey = `${className}_${context.json[idFieldName]}`;
      if (cache.has(cacheKey)) {
        return cache.get(cacheKey);
      }
      const model = defFactory(context);
      cache.set(cacheKey, model);
      return model;
    }
    return defFactory(context);
  });
}

export function cacheable(cacheKey: string) {
  // eslint-disable-next-line @typescript-eslint/ban-types
  return (constructor: Function) => {
    // eslint-disable-next-line no-param-reassign
    (constructor as any).cacheKey = cacheKey;
  };
}
